"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/feedback";
import { useAccessToken, useTenantData } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import {
  DEFAULT_STUDIO_OPERATIONS_SETTINGS,
  DEFAULT_STUDIO_OPERATIONS_SETTINGS_PERSISTED,
} from "@/lib/utils/operations-settings/operations-settings-defaults";
import {
  fetchStudioOperationsSettings,
  upsertStudioOperationsSettings,
  type StudioOperationsSettingsResult,
} from "@/lib/utils/operations-settings/operations-settings-api";

type UseStudioOperationsSettingsOptions = {
  enabled?: boolean;
};

export function useStudioOperationsSettings({
  enabled = true,
}: UseStudioOperationsSettingsOptions = {}) {
  const { tenantId } = useTenantData();
  const accessToken = useAccessToken();

  const query = useQuery({
    queryKey: [QueryKeys.StudioOperationsSettings, tenantId],
    queryFn: () => {
      if (!tenantId) {
        return {
          ...DEFAULT_STUDIO_OPERATIONS_SETTINGS,
          persisted: { ...DEFAULT_STUDIO_OPERATIONS_SETTINGS_PERSISTED },
        } satisfies StudioOperationsSettingsResult;
      }
      return fetchStudioOperationsSettings(tenantId, {
        accessToken: accessToken ?? undefined,
      });
    },
    enabled: enabled && !!tenantId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  return {
    lowEnrollmentThreshold:
      query.data?.lowEnrollmentThreshold ??
      DEFAULT_STUDIO_OPERATIONS_SETTINGS.lowEnrollmentThreshold,
    teacherLoadLightMax:
      query.data?.teacherLoadLightMax ??
      DEFAULT_STUDIO_OPERATIONS_SETTINGS.teacherLoadLightMax,
    teacherLoadPartTimeMax:
      query.data?.teacherLoadPartTimeMax ??
      DEFAULT_STUDIO_OPERATIONS_SETTINGS.teacherLoadPartTimeMax,
    teacherLoadFullTimeMax:
      query.data?.teacherLoadFullTimeMax ??
      DEFAULT_STUDIO_OPERATIONS_SETTINGS.teacherLoadFullTimeMax,
    persisted: query.data?.persisted ?? {
      ...DEFAULT_STUDIO_OPERATIONS_SETTINGS_PERSISTED,
    },
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useStudioOperationsSettingsUpdate() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  return useMutation({
    mutationFn: async (
      request: StudioOperationsSettingsResult,
    ): Promise<StudioOperationsSettingsResult> => {
      if (!accessToken || !tenantId) {
        throw new Error("Authentication token is required");
      }
      return upsertStudioOperationsSettings(tenantId, request, accessToken);
    },
    onSuccess: async () => {
      if (tenantId) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.StudioOperationsSettings, tenantId],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.WidgetData],
        });
      }

      toast({
        status: "success",
        title: "Operations settings updated",
        description: "Dashboard thresholds have been saved for your studio.",
      });
    },
    onError: (error: unknown) => {
      toast({
        status: "error",
        title: "Could not save operations settings",
        description:
          error instanceof Error ? error.message : "Something went wrong.",
      });
    },
  });
}
